import React, { useEffect, useState, useCallback } from 'react';
import { apiCall } from '../lib/api';
import { cn } from '../lib/utils';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { MonitorPicker } from '../components/MonitorPicker';

interface NovaAccount {
  id: string;
  source_system: string;
  nova_api_url: string;
  username: string | null;
  username_secret_ref: string | null;
  password_secret_ref: string | null;
  has_password: boolean;
  monitor_uid: string;
  site_ids: string | null;
  discovered_site_ids: string | null;
  group_ids: string | null;
  workflow: string | null;
  poll_interval_seconds: number | null;
  allow_dev_default_policy: boolean | null;
  enable_repeat_alerts: boolean | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface SourceSystem {
  id: string;
  name: string;
  enabled: boolean;
}

interface FormData {
  id: string;
  source_system: string;
  nova_api_url: string;
  username: string;
  username_secret_ref: string;
  password: string;
  password_secret_ref: string;
  monitor_uid: string;
  group_ids: string;
  workflow: string;
  poll_interval_seconds: string;
  allow_dev_default_policy: boolean;
  enable_repeat_alerts: boolean;
  enabled: boolean;
}

const DEFAULT_POLL_INTERVAL_SECONDS = '1';

function generateAccountId(): string {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).substring(2, 6);
  return `acct-${ts}-${rand}`;
}

const emptyForm: FormData = {
  id: '', source_system: '', nova_api_url: 'https://validation.wetsens.com',
  username: '', username_secret_ref: '', password: '', password_secret_ref: '',
  monitor_uid: '', group_ids: '', workflow: '',
  poll_interval_seconds: DEFAULT_POLL_INTERVAL_SECONDS,
  allow_dev_default_policy: true, enable_repeat_alerts: false, enabled: true,
};

export const NovaAccountsPage: React.FC = () => {
  const [accounts, setAccounts] = useState<NovaAccount[]>([]);
  const [sourceSystems, setSourceSystems] = useState<SourceSystem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const loadAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await apiCall('/api/config/accounts');
      if (resp.ok) {
        const data = await resp.json();
        setAccounts(data.accounts || []);
      }
    } catch {} finally { setLoading(false); }
  }, []);

  const loadSourceSystems = useCallback(async () => {
    try {
      const resp = await apiCall('/api/config/sources');
      if (resp.ok) {
        const data = await resp.json();
        setSourceSystems(data.source_systems || []);
      }
    } catch {}
  }, []);

  useEffect(() => {
    loadAccounts();
    loadSourceSystems();
  }, [loadAccounts, loadSourceSystems]);

  const getDefaultSourceSystem = useCallback(() => {
    return sourceSystems.find(s => s.enabled)?.id || sourceSystems[0]?.id || '';
  }, [sourceSystems]);

  useEffect(() => {
    if (!showForm || editingId || form.source_system) return;
    const sourceSystem = getDefaultSourceSystem();
    if (sourceSystem) setForm(f => ({ ...f, source_system: f.source_system || sourceSystem }));
  }, [showForm, editingId, form.source_system, getDefaultSourceSystem]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, id: generateAccountId(), source_system: getDefaultSourceSystem() });
    setError(null);
    setShowForm(true);
  };

  const openEdit = (a: NovaAccount) => {
    setEditingId(a.id);
    setForm({
      id: a.id,
      source_system: a.source_system || getDefaultSourceSystem(),
      nova_api_url: a.nova_api_url,
      username: a.username || '',
      username_secret_ref: a.username_secret_ref || '',
      password: '',
      password_secret_ref: a.password_secret_ref || '',
      monitor_uid: a.monitor_uid,
      group_ids: a.group_ids || '',
      workflow: a.workflow || '',
      poll_interval_seconds: a.poll_interval_seconds ? String(a.poll_interval_seconds) : DEFAULT_POLL_INTERVAL_SECONDS,
      allow_dev_default_policy: a.allow_dev_default_policy ?? true,
      enable_repeat_alerts: a.enable_repeat_alerts ?? false,
      enabled: a.enabled,
    });
    setError(null);
    setShowForm(true);
  };

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      if (!form.source_system) throw new Error('Select a source system first');
      const url = editingId ? `/api/config/accounts/${editingId}` : '/api/config/accounts';
      const method = editingId ? 'PUT' : 'POST';
      const body: any = { ...form };
      body.poll_interval_seconds = form.poll_interval_seconds.trim() ? Number(form.poll_interval_seconds) : Number(DEFAULT_POLL_INTERVAL_SECONDS);
      if (editingId && !body.password) delete body.password;
      if (editingId && !body.password_secret_ref && !body.password) delete body.password_secret_ref;
      const resp = await apiCall(url, { method, body: JSON.stringify(body) });
      if (!resp.ok) { const d = await resp.json(); throw new Error(d.error || 'Failed'); }
      setShowForm(false);
      loadAccounts();
    } catch (err: any) { setError(err.message); } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await apiCall(`/api/config/accounts/${deleteTarget}`, { method: 'DELETE' });
    setDeleteTarget(null);
    loadAccounts();
  };

  const sourceSystemOptions = sourceSystems.some(s => s.id === form.source_system)
    ? sourceSystems
    : form.source_system
      ? [...sourceSystems, { id: form.source_system, name: 'Current source', enabled: true }]
      : sourceSystems;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Nova Accounts</h1>
          <p className="text-sm text-gray-500 mt-1">Manage Nova API credentials used by the poller. Tenant assignment is managed on the Tenants page.</p>
        </div>
        <button onClick={openCreate} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
          Add Account
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold">{editingId ? `Edit: ${editingId}` : 'New Account'}</h2>
          {error && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{error}</div>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {!editingId && (
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Account ID</label>
                <div className="flex gap-2">
                  <input
                    className="flex-1 border rounded-lg px-3 py-2 text-sm"
                    value={form.id}
                    onChange={e => {
                      const id = e.target.value;
                      setForm(f => {
                        const matchingSource = sourceSystems.find(s => s.id === id && s.enabled);
                        return { ...f, id, source_system: matchingSource && !f.source_system ? matchingSource.id : f.source_system };
                      });
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, id: generateAccountId() }))}
                    className="px-3 py-2 border rounded-lg text-xs text-gray-600 hover:bg-gray-50 whitespace-nowrap"
                  >
                    Regenerate
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-400">Auto-generated. You can override with a custom ID.</p>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Source System</label>
              <select
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={form.source_system}
                onChange={e => setForm(f => ({ ...f, source_system: e.target.value }))}
                disabled={sourceSystemOptions.length === 0}
              >
                {sourceSystemOptions.length === 0 ? (
                  <option value="">No source systems</option>
                ) : (
                  sourceSystemOptions.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.id} - {s.name}{s.enabled ? '' : ' (disabled)'}
                    </option>
                  ))
                )}
              </select>
              <p className="mt-1 text-xs text-gray-400">Routing key prefix. Account aquina + tenant aquina:450 publishes event.aquina.450.</p>
              {sourceSystemOptions.length === 0 && (
                <p className="mt-1 text-xs text-red-500">Add a source system before creating a Nova account.</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Nova API URL</label>
              <input className="w-full border rounded-lg px-3 py-2 text-sm" value={form.nova_api_url} onChange={e => setForm(f => ({ ...f, nova_api_url: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Username</label>
              <input className="w-full border rounded-lg px-3 py-2 text-sm" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Username Secret Ref</label>
              <input
                className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                value={form.username_secret_ref}
                onChange={e => setForm(f => ({ ...f, username_secret_ref: e.target.value }))}
                placeholder="env:NOVA_USERNAME"
              />
              <p className="mt-1 text-xs text-gray-400">Optional alternative to the direct username field.</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Password {editingId && '(leave blank to keep)'}</label>
              <input type="password" className="w-full border rounded-lg px-3 py-2 text-sm" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Password Secret Ref</label>
              <input
                className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                value={form.password_secret_ref}
                onChange={e => setForm(f => ({ ...f, password_secret_ref: e.target.value }))}
                placeholder="file:/var/run/secrets/nova/password"
              />
              <p className="mt-1 text-xs text-gray-400">Optional alternative to the direct password field. Use env:VAR_NAME or file:/path/to/secret.</p>
            </div>
            <div className="md:col-span-2">
              <MonitorPicker
                key={editingId ?? 'new'}
                accountId={form.id}
                isSaved={!!editingId}
                credentials={{
                  novaApiUrl: form.nova_api_url,
                  username: form.username,
                  usernameSecretRef: form.username_secret_ref,
                  password: form.password,
                  passwordSecretRef: form.password_secret_ref,
                }}
                value={form.monitor_uid}
                onChange={(monitorUid) => setForm(f => ({ ...f, monitor_uid: monitorUid }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Group IDs</label>
              <input className="w-full border rounded-lg px-3 py-2 text-sm" value={form.group_ids} onChange={e => setForm(f => ({ ...f, group_ids: e.target.value }))} placeholder="optional" />
              <p className="mt-1 text-xs text-gray-400">Commas, spaces, dots, or line breaks are okay.</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Workflow</label>
              <input className="w-full border rounded-lg px-3 py-2 text-sm" value={form.workflow} onChange={e => setForm(f => ({ ...f, workflow: e.target.value }))} placeholder="optional" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Poll Interval (seconds)</label>
              <input
                type="number"
                min="1"
                step="1"
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={form.poll_interval_seconds}
                onChange={e => setForm(f => ({ ...f, poll_interval_seconds: e.target.value }))}
                placeholder={DEFAULT_POLL_INTERVAL_SECONDS}
              />
              <p className="mt-1 text-xs text-gray-400">Default is 1 second.</p>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 mt-6">
              <input
                type="checkbox"
                checked={form.allow_dev_default_policy}
                onChange={e => setForm(f => ({ ...f, allow_dev_default_policy: e.target.checked }))}
              />
              Allow dev default policy
            </label>
            {/*
              Repeat-alerts master switch + cadence are now configured per partner
              (with optional tenant override) on the Partners / Tenants pages.
              The legacy account-level toggle is hidden but preserved on the record
              for compatibility until the column is dropped.
            */}
            <label className="flex items-center gap-2 text-sm text-gray-700 mt-6">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))}
              />
              Enabled
            </label>
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={handleSave} disabled={saving || !form.source_system} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-400">Loading...</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm table-fixed">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Account ID</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Source</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">API URL</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Username</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Monitor UID</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Sites</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Poll</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Behavior</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Status</th>
                <th className="text-right px-5 py-3 font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map(a => (
                <tr key={a.id} className="border-b border-gray-100 last:border-0">
                  <td className="px-5 py-3 font-mono text-gray-700">{a.id}</td>
                  <td className="px-5 py-3 font-mono text-gray-500">{a.source_system || 'nova'}</td>
                  <td className="px-5 py-3 text-gray-600 truncate max-w-[200px]">{a.nova_api_url}</td>
                  <td className="px-5 py-3 text-gray-600 break-all max-w-[200px]">{a.username || a.username_secret_ref || '--'}</td>
                  <td className="px-5 py-3 text-gray-500">{a.monitor_uid}</td>
                  <td className="px-5 py-3 text-gray-500">
                    {a.discovered_site_ids
                      ? `${a.discovered_site_ids.split(',').length} site(s)`
                      : (a.site_ids || 'via tenants')}
                  </td>
                  <td className="px-5 py-3 text-gray-500">{a.poll_interval_seconds || DEFAULT_POLL_INTERVAL_SECONDS}s</td>
                  <td className="px-5 py-3 text-gray-500 text-xs">
                    dev policy {a.allow_dev_default_policy ? 'on' : 'off'}
                  </td>
                  <td className="px-5 py-3">
                    <span className={cn('text-xs font-medium px-2 py-0.5 rounded', a.enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500')}>
                      {a.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right space-x-2">
                    <button onClick={() => openEdit(a)} className="text-blue-600 hover:text-blue-800 text-xs font-medium">Edit</button>
                    <button onClick={() => setDeleteTarget(a.id)} className="text-red-600 hover:text-red-800 text-xs font-medium">Delete</button>
                  </td>
                </tr>
              ))}
              {accounts.length === 0 && (
                <tr><td colSpan={10} className="px-5 py-8 text-center text-gray-400">No accounts configured. Add one to get started.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Are you sure you want to delete this?"
        message={`This will permanently delete the account "${deleteTarget}".`}
        warning="All tenants associated with this account will also be deleted, including their locations and partner assignments. This action cannot be undone."
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};
